import React, {useEffect, useMemo, useRef, forwardRef, useImperativeHandle} from 'react';
import {useField} from '@unform/core';
import Select, {components} from 'react-select';
import {SELECT_CLASS_PREFIX} from './prefix';
import PropTypes from 'prop-types';
import './react-select.sass';

const ReactSelectNotScoped = forwardRef((props, ref) => {
    const innerRef = useRef(null);

    const {
        name,
        // eslint-disable-next-line no-unused-vars
        label,
        options,
        multiple,
        onChange,
        isDisabled,
        value,
        ...rest
    } = props;

    useImperativeHandle(ref, () => ({
        setValue: innerRef.current?.select?.setValue,
        getValue: () => innerRef.current.state.value,
    }), []);

    const _defaultValue = useMemo(() => {
        if (!value) return null;

        if (!multiple) {
            return options.find((option) => option.id === value);
        }

        return options.filter((option) => value.includes(option.id));
    }, []);

    return (<Select
            classNamePrefix={SELECT_CLASS_PREFIX}
            name={name}
            aria-label={name}
            options={options}
            isMulti={multiple}
            isDisabled={isDisabled}
            defaultValue={_defaultValue}
            ref={innerRef}
            onChange={onChange}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.title}
            components={{
                DropdownIndicator,
                IndicatorSeparator,
                Control,
            }}
            isClearable
            {...rest}
        />
    );
});

ReactSelectNotScoped.displayName = 'ReactSelectNotScoped';

const ReactSelectScoped = forwardRef((props, ref) => {
    const innerRef = useRef(null);

    const {
        name,
        label,
        options,
        multiple,
        onChange,
        isDisabled,
        keyField,
        ...rest
    } = props;

    useImperativeHandle(ref, () => ({
        setValue: innerRef.current?.select?.setValue,
        getValue: () => innerRef.current.state.value,
    }), []);

    const {fieldName, registerField, defaultValue, error} = useField(name);

    const parseSelectValue = (selectRef) => {
        const selectValue = selectRef.state.value;
        if (!multiple) {
            return selectValue ? selectValue.id : undefined;
        }

        return selectValue ? selectValue.map((option) => option.id) : [];
    };

    useEffect(() => {
        registerField({
            name: fieldName,
            ref: innerRef.current,
            path: 'state.value',
            getValue: (ref) => {
                if (multiple) {
                    if (!ref.state.value) {
                        return undefined;
                    }
                    return ref.state.value.map((option) => option.id);
                }
                if (!ref.state.value) {
                    return undefined;
                }
                return ref.state.value[keyField];
            },
            parseValue: parseSelectValue,
            setValue: (ref, value) => {
                const _value = options.find((option) => option[keyField] === value);
                if (_value) {
                    ref.select.setValue(_value);
                }
            },
            clearValue: (ref) => {
                ref.select.clearValue();
            },
        });
    }, [fieldName, registerField, multiple, options, keyField]);

    const _defaultValue = useMemo(() => {
        if (!defaultValue) return null;

        if (!multiple) {
            return options.find((option) => option.id === defaultValue);
        }

        return options.filter((option) => defaultValue.includes(option.id));
    }, []);

    return (
        <>
            {label && <label htmlFor={fieldName}>{label}</label>}

            <Select

                classNamePrefix={SELECT_CLASS_PREFIX}
                name={fieldName}
                aria-label={fieldName}
                options={options}
                isMulti={multiple}
                isDisabled={isDisabled}
                defaultValue={_defaultValue}
                ref={innerRef}
                onChange={onChange}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.title}
                components={{
                    DropdownIndicator,
                    IndicatorSeparator,
                    Control,
                }}
                isClearable
                {...rest}
            />

            {error && <span className="error">{error}</span>}
        </>
    );
});

ReactSelectScoped.displayName = 'ReactSelectScoped';

const InnerReactSelect = forwardRef((props, ref) => {
    const {
        scoped,
        ...rest
    } = props;

    if (!scoped) {
        return <ReactSelectNotScoped {...rest} ref={ref} />;
    }

    return <ReactSelectScoped {...rest} ref={ref} />;
});

InnerReactSelect.displayName = 'InnerReactSelect';

InnerReactSelect.defaultProps = {
    keyField: 'id',
    scoped: true,
};

export default InnerReactSelect;

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <span className="far fa-chevron-down" style={{color: 'black'}}/>
        </components.DropdownIndicator>
    );
};

const IndicatorSeparator = () => null;

const Control = (props) => (
    <components.Control
        {...props}
        className="custom-shadow rounded-pill px-2 select"
    />
);

// Controlled wrapper over the non-scoped select: the inner select only reads its initial value, so it
// is remounted when the value changes. Option ids may be '' (e.g. an "all" option).
export const SelectField = ({id, label, value, options, onChange, ...rest}) => (
    <InnerReactSelect
        key={`${id}:${value}`}
        scoped={false}
        name={id}
        inputId={id}
        aria-label={label}
        options={options}
        defaultValue={options.find((option) => option.id === value) || null}
        onChange={(option) => onChange(option ? option.id : '')}
        isClearable={false}
        {...rest}
    />
);

SelectField.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    options: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
    })).isRequired,
    onChange: PropTypes.func.isRequired,
};
